import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { floorplansApi } from "@/lib/api/client";
import type { NavigateResponse, NearestExitKind } from "@/lib/api/client";
import { NearestExitControl } from "@/components/floorplan/NearestExitControl";
import { nearestExitErrorMessage, parseExitParam, withExitParams } from "@/lib/nearest-exit";
import { FloorplanViewer, withConnectionStops, type FloorplanViewerHandle } from "@nodus/floorplan";
import { useFloorAlarms } from "@/hooks/use-floor-alarms";
import { FLOOR_ALARM_EVENT, isRegionOnlyEvent, liveAlarmLocations } from "@/lib/floor-alarms";
import { usePortalEvent } from "@/lib/portal-socket";
import { FloorplanRoomPanel } from "@/components/floorplan/FloorplanRoomPanel";
import { MultiFloorNavigation } from "@/components/floorplan/MultiFloorNavigation";
import type { FloorplanRoom } from "@nodus/floorplan";
import { Text, Box, Flex } from "@mantine/core";
import { toFloorplanRoom, type ApiRoom, type FloorplanDetail, type FloorplanListItem } from "@/lib/floorplan-rooms";
import { SectionGuides } from "@/components/guides/section-guides";

export default function FloorplanPage() {
  const [, setSearchParams] = useSearchParams();
  const viewerRef = useRef<FloorplanViewerHandle>(null);
  const queryClient = useQueryClient();

  // Capture deep-link params once on mount (x/y/z handled by FloorplanViewer syncUrl)
  const initialParams = useRef(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      roomId: p.get("room") ? Number(p.get("room")) : null,
      fpId: p.get("fp") ? Number(p.get("fp")) : null,
      toRoomId: p.get("to") ? Number(p.get("to")) : null,
      fromRoomId: p.get("from") ? Number(p.get("from")) : null,
      navView: p.get("view") as "map" | "directions" | null,
      // ?exit=any|emergency — route to the nearest exit on load (#473)
      exitKind: parseExitParam(p.get("exit")),
    };
  }).current();
  const deepLinkRef = useRef({ ...initialParams, handled: false, exitHandled: false });

  const [selectedFloorplanId, setSelectedFloorplanId] = useState<number | null>(
    deepLinkRef.current.fpId,
  );
  const [selectedRoom, setSelectedRoom] = useState<ApiRoom | null>(null);
  const [highlightedRoomId, setHighlightedRoomId] = useState<number | null>(null);
  const [multiFloorResult, setMultiFloorResult] = useState<NavigateResponse | null>(null);
  const [navFromRoomId, setNavFromRoomId] = useState<number | null>(null);

  const [navError, setNavError] = useState<string | null>(null);
  const navigateMutation = useMutation({
    mutationFn: ({ toRoomId, fromRoomId }: { toRoomId: number; fromRoomId?: number }) =>
      floorplansApi.navigate(toRoomId, fromRoomId),
    onSuccess: (res) => { setMultiFloorResult(res.data); setNavError(null); },
    onError: (err: any) => {
      const status = err?.response?.status;
      if (status === 404) {
        setNavError("No route found between these rooms. They may be on disconnected floors.");
      } else {
        setNavError("Failed to compute route. Please try again.");
      }
    },
  });

  // Nearest exit (#473): the strip's switch, and the route it asks for.
  const [emergencyOnly, setEmergencyOnly] = useState(initialParams.exitKind === "emergency");
  const [exitError, setExitError] = useState<string | null>(null);
  const nearestExitMutation = useMutation({
    mutationFn: ({ kind, fromRoomId }: { kind: NearestExitKind; fromRoomId?: number }) =>
      floorplansApi.nearestExit(kind, fromRoomId),
    onSuccess: (res) => { setMultiFloorResult(res.data); setExitError(null); setNavError(null); },
    onError: (err, { kind }) => setExitError(nearestExitErrorMessage(err, kind)),
  });

  const { data: floorplanList } = useQuery({
    queryKey: ["floorplans"],
    queryFn: () => floorplansApi.list().then((r) => r.data as FloorplanListItem[]),
    staleTime: 5 * 60 * 1000,
  });

  // Auto-select floorplan with most rooms (or first)
  const activeFloorplanId = selectedFloorplanId ?? (() => {
    if (!floorplanList?.length) return null;
    const best = [...floorplanList].sort((a, b) => (b.room_count ?? 0) - (a.room_count ?? 0));
    return best[0]?.id ?? null;
  })();

  const { data: floorplan, isLoading, error: floorplanError } = useQuery({
    queryKey: ["floorplan", activeFloorplanId],
    queryFn: () => floorplansApi.get(activeFloorplanId!).then((r) => r.data as FloorplanDetail),
    enabled: activeFloorplanId != null,
    staleTime: 60_000,
    retry: (count, err) =>
      (err as { response?: { status?: number } })?.response?.status === 403 ? false : count < 2,
  });

  // 403 = floorplan is access-limited and the user can't reach any of its
  // floors via Axema (#165). The list already hides these, so this only
  // happens on a stale link / direct URL.
  const floorplanAccessDenied =
    (floorplanError as { response?: { status?: number } } | null)?.response?.status === 403;

  const { data: myRoom } = useQuery({
    queryKey: ["my-room"],
    queryFn: () => floorplansApi.getMyRoom().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const rooms = useMemo(() => (floorplan?.rooms ?? []).map(toFloorplanRoom), [floorplan]);

  // Alarm per drawn region (each floor, address, room with its own links), never the whole plan.
  // Floors follow the pushed list at once; a room / address push refetches the plan.
  const floorAlarms = useFloorAlarms();
  const alarmLocations = useMemo(
    () => liveAlarmLocations(floorplan?.alarm_regions, floorAlarms, (floorplan?.floors ?? []).map((f) => f.floor_id)),
    [floorplan, floorAlarms],
  );
  usePortalEvent(FLOOR_ALARM_EVENT, (event) => {
    if (isRegionOnlyEvent(event)) void queryClient.invalidateQueries({ queryKey: ["floorplan"] });
  });

  // Deep-link: ?to=123&from=456 — auto-navigate on load
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (dl.handled || !dl.toRoomId) return;
    dl.handled = true;
    navigateMutation.mutate({
      toRoomId: dl.toRoomId,
      fromRoomId: dl.fromRoomId ?? myRoom?.room_id ?? undefined,
    });
  }, [myRoom, navigateMutation]);

  // Deep-link: ?room=123 — select room and zoom
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (dl.handled || !dl.roomId || !floorplan) return;

    const apiRoom = floorplan.rooms.find((r) => r.id === dl.roomId);
    if (apiRoom) {
      dl.handled = true;
      setSelectedRoom(apiRoom);

      setTimeout(() => {
        if (!apiRoom.floorplan_polygon?.length) return;
        const poly = apiRoom.floorplan_polygon[0];
        const xs = poly.map((p: number[]) => p[0]);
        const ys = poly.map((p: number[]) => p[1]);
        viewerRef.current?.zoomToRect(
          Math.min(...xs), Math.min(...ys),
          Math.max(...xs), Math.max(...ys),
          60,
        );
      }, 500);
      return;
    }

    // Room not on current floorplan — switch to the correct one
    if (floorplanList && floorplanList.length > 1) {
      if (dl.fpId && dl.fpId !== floorplan.id) {
        setSelectedFloorplanId(dl.fpId);
      } else {
        const currentIdx = floorplanList.findIndex((fp) => fp.id === floorplan.id);
        const nextFp = floorplanList.find((_fp, i) => i !== currentIdx);
        if (nextFp && selectedFloorplanId !== nextFp.id) {
          setSelectedFloorplanId(nextFp.id);
        }
      }
    }
  }, [floorplan, floorplanList, selectedFloorplanId, setSearchParams]);

  // Pre-fill navFromRoomId with user's assigned room
  useEffect(() => {
    if (myRoom?.room_id && !navFromRoomId) {
      setNavFromRoomId(myRoom.room_id);
    }
  }, [myRoom, navFromRoomId]);

  const handleNavigate = useCallback(
    (roomId: number, fromRoomId?: number) => {
      const from = fromRoomId ?? navFromRoomId ?? myRoom?.room_id ?? undefined;
      navigateMutation.mutate({
        toRoomId: roomId,
        fromRoomId: from,
      });
      // Update URL with navigation params
      const params = new URLSearchParams(window.location.search);
      params.delete("exit");
      params.set("to", String(roomId));
      if (from) params.set("from", String(from));
      setSearchParams(params, { replace: true });
    },
    [myRoom, navFromRoomId, navigateMutation, setSearchParams],
  );

  // Starts at the given room (the room sheet's own), else the picked "Navigate from" room,
  // else the member's own room (the backend falls back to it too).
  const handleNearestExit = useCallback(
    (kind: NearestExitKind, fromRoomId?: number) => {
      const from = fromRoomId ?? navFromRoomId ?? myRoom?.room_id ?? undefined;
      setExitError(null);
      nearestExitMutation.mutate({ kind, fromRoomId: from });
      setSearchParams(withExitParams(window.location.search, kind, from), { replace: true });
    },
    [myRoom, navFromRoomId, nearestExitMutation, setSearchParams],
  );

  // Deep-link: ?exit=any|emergency[&from=123] — route to the nearest exit once the member's room is known.
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (dl.exitHandled || !dl.exitKind) return;
    if (!dl.fromRoomId && myRoom === undefined) return; // wait for /my-room/
    dl.exitHandled = true;
    nearestExitMutation.mutate({ kind: dl.exitKind, fromRoomId: dl.fromRoomId ?? myRoom?.room_id ?? undefined });
  }, [myRoom, nearestExitMutation]);

  const handleRoomClick = (room: FloorplanRoom | null) => {
    if (!room) {
      setSelectedRoom(null);
      const params = new URLSearchParams(window.location.search);
      params.delete("room");
      setSearchParams(params, { replace: true });
      return;
    }
    const apiRoom = floorplan?.rooms.find((r) => r.id === room.id) ?? null;
    setSelectedRoom(apiRoom);
    const params = new URLSearchParams(window.location.search);
    if (apiRoom) {
      params.set("room", String(apiRoom.id));
    } else {
      params.delete("room");
    }
    setSearchParams(params, { replace: true });
  };

  const handleClosePanel = () => {
    setSelectedRoom(null);
    const params = new URLSearchParams(window.location.search);
    params.delete("room");
    setSearchParams(params, { replace: true });
  };

  return (
    <Flex direction="column" h="calc(100dvh - 100px)">
      {/* 100px = header 56px + bottom nav 44px */}
      {/* Setup guides pinned to the floorplan (#436): a strip above the viewer
          (an overlay at the bottom sat under the fixed nav); nothing when none */}
      <SectionGuides area="floorplan" inset />
      {floorplan && (
        <NearestExitControl
          emergencyOnly={emergencyOnly}
          onEmergencyOnlyChange={setEmergencyOnly}
          onFind={(kind) => handleNearestExit(kind)}
          pending={nearestExitMutation.isPending}
          error={exitError}
        />
      )}
      {/* Viewer */}
      <Box pos="relative" mih={0} flex={1}>
        {isLoading && (
          <Flex align="center" justify="center" h="100%">
            <Text fz="sm" c="dimmed">Loading floorplan...</Text>
          </Flex>
        )}

        {!isLoading && !floorplan && (
          <Flex align="center" justify="center" h="100%">
            <Text fz="sm" c="dimmed" ta="center">
              {floorplanAccessDenied
                ? "You don't have access to this floorplan. Contact your office manager if you think this is wrong."
                : "No floorplans available."}
            </Text>
          </Flex>
        )}

        {floorplan && (
          <FloorplanViewer
            ref={viewerRef}
            svg={floorplan.svg_content}
            rooms={rooms}
            scale={floorplan.scale}
            selectedRoomId={selectedRoom?.id ?? highlightedRoomId}
            relatedRoomIds={myRoom?.room_ids?.length ? myRoom.room_ids : undefined}
            onRoomClick={handleRoomClick}
            hidePanel
            syncUrl
            alarmLocations={alarmLocations}
            floors={(floorplan.floors ?? []).map((f: any) => ({
              floorId: f.floor_id,
              floorName: f.floor_name,
              addressId: f.address_id,
              addressName: f.address_name,
              region: f.region || [],
            }))}
            searchable
            onSearchResult={setHighlightedRoomId}
            floorFurniture={floorplan.furniture ?? []}
            doors={((floorplan as any).doors ?? []).map((d: any) => ({
              id: d.id,
              name: d.name,
              doorType: d.door_type,
              geometry: d.geometry,
              arrowSides: d.arrow_sides,
              floorId: d.floor_id,
            }))}
            outlets={((floorplan as any).outlets ?? []).map((o: any) => ({
              id: o.id,
              name: o.name,
              geometry: o.geometry,
              floorId: o.floor_id,
              roomId: o.room_id,
              networkId: o.network_id,
              medium: o.medium,
              status: o.status,
            }))}
            availableLayers={((floorplan as any).available_layers ?? []).map((l: any) => ({
              id: l.id,
              name: l.name,
              type: l.type,
              defaultOn: l.default_on,
              alwaysShow: l.always_show ?? false,
              order: l.order,
              filter: l.filter,
              parentId: l.parent_id ?? null,
            }))}
            connectionZones={withConnectionStops(floorplan.connection_zones, floorplan.floor_connections)}
            floorplans={floorplanList}
            activeFloorplanId={activeFloorplanId ?? undefined}
            onFloorplanChange={(fpId: number) => {
              setSelectedFloorplanId(fpId);
              setSelectedRoom(null);
              const params = new URLSearchParams(window.location.search);
              params.set("fp", String(fpId));
              params.delete("room");
              params.delete("x");
              params.delete("y");
              params.delete("z");
              setSearchParams(params, { replace: true });
            }}
          />
        )}

        {/* Side panel */}
        <FloorplanRoomPanel
          room={selectedRoom}
          opened={!!selectedRoom}
          onClose={handleClosePanel}
          onNavigate={handleNavigate}
          navigationResult={multiFloorResult}
          navigationError={navError}
          onClearNavigation={() => {
            setMultiFloorResult(null);
            setNavError(null);
            navigateMutation.reset();
          }}
          allRooms={(floorplan?.rooms ?? []).filter(r => r.floorplan_polygon?.length)}
          navFromRoomId={navFromRoomId}
          onNavFromRoomChange={setNavFromRoomId}
          isNavigating={navigateMutation.isPending}
          onNearestExit={(fromRoomId) => handleNearestExit(emergencyOnly ? "emergency" : "any", fromRoomId)}
          isFindingExit={nearestExitMutation.isPending}
        />
      </Box>

      {multiFloorResult && (
        <MultiFloorNavigation
          result={multiFloorResult}
          onClose={() => {
            setMultiFloorResult(null);
            navigateMutation.reset();
            nearestExitMutation.reset();
            const params = new URLSearchParams(window.location.search);
            params.delete("to");
            params.delete("from");
            params.delete("view");
            params.delete("exit");
            setSearchParams(params, { replace: true });
          }}
        />
      )}
    </Flex>
  );
}
